import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import type { CartItem, Product, User } from "./types";
import { api } from "./api";

type StoreContextValue = {
  cart: CartItem[];
  cartCount: number;
  cartOpen: boolean;
  setCartOpen: (open: boolean) => void;
  addToCart: (product: Product, options?: { size?: string; color?: string; quantity?: number }) => void;
  updateQuantity: (index: number, quantity: number) => void;
  removeFromCart: (index: number) => void;
  clearCart: () => void;
  favorites: string[];
  toggleFavorite: (id: string) => void;
  user: User | null;
  setUser: (user: User | null) => void;
  refreshUser: () => Promise<void>;
};

const StoreContext = createContext<StoreContextValue | null>(null);

const readStorage = <T,>(key: string, fallback: T): T => {
  try {
    return JSON.parse(localStorage.getItem(key) || "") as T;
  } catch {
    return fallback;
  }
};

export function StoreProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartItem[]>(() => readStorage("dm-cart", []));
  const [favorites, setFavorites] = useState<string[]>(() => readStorage("dm-favorites", []));
  const [cartOpen, setCartOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    localStorage.setItem("dm-cart", JSON.stringify(cart));
  }, [cart]);
  useEffect(() => {
    localStorage.setItem("dm-favorites", JSON.stringify(favorites));
  }, [favorites]);

  const refreshUser = async () => {
    try {
      const result = await api.get<{ user: User | null }>("/api/auth/me");
      setUser(result.user);
    } catch {
      setUser(null);
    }
  };

  useEffect(() => {
    void refreshUser();
  }, []);

  const value = useMemo<StoreContextValue>(
    () => ({
      cart,
      cartCount: cart.reduce((total, item) => total + item.quantity, 0),
      cartOpen,
      setCartOpen,
      addToCart: (product, options = {}) => {
        setCart((current) => {
          const quantity = options.quantity || 1;
          const index = current.findIndex(
            (item) =>
              item.product.id === product.id &&
              item.size === options.size &&
              item.color === options.color
          );
          if (index < 0) return [...current, { product, quantity, size: options.size, color: options.color }];
          return current.map((item, itemIndex) =>
            itemIndex === index ? { ...item, quantity: item.quantity + quantity } : item
          );
        });
        setCartOpen(true);
      },
      updateQuantity: (index, quantity) =>
        setCart((current) =>
          current
            .map((item, itemIndex) => (itemIndex === index ? { ...item, quantity } : item))
            .filter((item) => item.quantity > 0)
        ),
      removeFromCart: (index) =>
        setCart((current) => current.filter((_, itemIndex) => itemIndex !== index)),
      clearCart: () => setCart([]),
      favorites,
      toggleFavorite: (id) =>
        setFavorites((current) =>
          current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
        ),
      user,
      setUser,
      refreshUser
    }),
    [cart, cartOpen, favorites, user]
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export const useStore = () => {
  const value = useContext(StoreContext);
  if (!value) throw new Error("StoreProvider ausente");
  return value;
};
